import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTitle } from '@gsa/afp-shared-ui-utils';
import { PageTitle, Spinner, NotFound } from '@gsa/afp-component-library';
import Breadcrumbs from '../../leasing/components/Breadcrumbs';
import VehicleRequestSteps from './create-vehicle-request-steps';
import SearchCustomerAccount from './select-customer-account/search-customer-account';
import { VEHICLE_REQUEST_TAB } from '../utils/additional-utils';
import { cloneDeep } from 'lodash';
import {
  useGetAllStandardItemsBySin,
  useDocumentManagementAttachments,
  useGetAdditionalRequest,
  useGetAdditionalRequestLazy,
} from '../../../requests/additionals-request';
import {
  resetAdditionalsState,
  setSelectedCustomerAccount,
  setSelectedReason,
  changeGarageAddress,
  setAdditionalsDraftData,
  setVehicleAcknowledged,
  setSelectedStandardItem,
  setSelectedAdditionalOptions,
  changeProjectedTowingWeight,
  setUsedForTowing,
  setAdditionalAttachments,
  setSupplementaryInformation,
  setAdditionalRequestActivities,
  setAdditionalsRequestStatus,
  changeAdditionalsStep,
  setAdditionalRequestId,
  setAgencyGuidanceDocument,
  setLastSin,
  setRequisitionId,
  setRequisitionNumber,
  setDeliquencyCheck,
} from '../../../reducers/additionals-reducer';
import {
  setSelectedVehicleType,
  setSelectedStandardItem as setLeasingStandardItem,
} from '../../../reducers/leasing';
import {
  getStandardItemForReferral,
  STANDARD_ITEM_PARENT_CATEGORY,
  ADDITIONAL_REQUEST_STATUS,
} from '../utils/additional-utils';
import { ADDITIONAL_VEHICLE_STEPS } from './create-vehicle-request-steps';
import './create-vehicle-request.scss';

export default function CreateAdditionalRequest() {
  useTitle('Additionals | Leasing');
  const { requestId } = useParams();
  const dispatch = useDispatch();
  const { selectedCustomerAccount, isCustomerChange } = useSelector(
    (state) => state.additionalsReducer,
  );
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [getAdditionalRequest, { loading: additionalRequestLoading }] =
    useGetAdditionalRequestLazy();
  const [getAllStandardItemsBySin] = useGetAllStandardItemsBySin();
  const [tab, _setTab] = useState(VEHICLE_REQUEST_TAB.SELECTION_DETAILS);

  const AGENCY_DOCUMENT_TYPE = 'agency_guidance';

  useDocumentManagementAttachments({
    apolloOptions: {
      skip: !selectedCustomerAccount?.agencyCode,
    },
    variables: {
      docType: AGENCY_DOCUMENT_TYPE,
      agencyCode: selectedCustomerAccount?.agencyCode,
    },
  }).then(({ data }) => {
    dispatch(setAgencyGuidanceDocument(data));
  });

  useEffect(() => {
    if (requestId) {
      setLoading(true);
      getAdditionalRequest({
        variables: {
          additionalRequestId: `${requestId}`,
        },
      })
        .then(async ({ data }) => {
          if (data) {
            dispatch(setAdditionalRequestId(data.additionalRequestId));
            dispatch(setAdditionalsDraftData(data));
            dispatch(setSelectedCustomerAccount(data.customerAccount));
            dispatch(setSelectedReason(data.requestType));
            dispatch(setAdditionalsRequestStatus(data?.requestStatusCode));
            dispatch(setVehicleAcknowledged(data.acknowledged));
            dispatch(setRequisitionId(data.requisitionId));
            dispatch(setRequisitionNumber(data.requisitionNumber));
            dispatch(setDeliquencyCheck(data.deliquencyCheck));
            const activities = cloneDeep(data.additionalRequestActivities);
            const sortedActivities = activities.sort((a, b) => {
              return new Date(b.createdAt) - new Date(a.createdAt);
            });
            if (activities?.length) {
              dispatch(setAdditionalRequestActivities(sortedActivities));
            }
            dispatch(
              setSelectedAdditionalOptions(data.additionalRequestOptions),
            );
            dispatch(
              changeGarageAddress({
                address1: data?.additionalRequestGarageAddress?.addressLine1,
                address2: data?.additionalRequestGarageAddress?.addressLine2,
                city: data?.additionalRequestGarageAddress?.city,
                country: data?.additionalRequestGarageAddress?.countryCode,
                state: data?.additionalRequestGarageAddress?.stateCode,
                zip: data?.additionalRequestGarageAddress?.zipcode,
              }),
            );

            if (data?.additionalRequestAttachments?.length > 0) {
              const attachments = cloneDeep(data?.additionalRequestAttachments);
              dispatch(
                setAdditionalAttachments(
                  attachments?.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
                  ),
                ),
              );
            }

            const towingWeight = data.towingWeight;
            const parsedTowingWeight = towingWeight?.match(/(\d+)\s*(lbs|kg)?/);

            if (parsedTowingWeight) {
              dispatch(setUsedForTowing(true));
              dispatch(
                changeProjectedTowingWeight({
                  value: parseInt(parsedTowingWeight[1]),
                  unit: parsedTowingWeight[2] || 'lbs',
                }),
              );
            }

            dispatch(
              setSupplementaryInformation({
                specialInstructions: data?.specificInstructions,
                missionMakeModel: data?.missionMakeModel,
                acknowledge: data?.acknowledgedSubmit,
              }),
            );

            const { data: allStandardItems } = await getAllStandardItemsBySin({
              variables: {
                filter: {
                  key: 'standardItemNumber',
                  operator: 'EQ',
                  value: data?.sin,
                },
              },
            });

            const standardItem = getStandardItemForReferral(
              allStandardItems,
              data.fiscalYear,
            );

            const tempStandardItem = { ...standardItem };
            tempStandardItem.id = parseInt(standardItem.id);

            dispatch(setSelectedStandardItem(tempStandardItem));
            dispatch(setLeasingStandardItem(tempStandardItem));
            const step =
              data?.requestStatusCode !== ADDITIONAL_REQUEST_STATUS.DRAFT
                ? ADDITIONAL_VEHICLE_STEPS.REVIEW_SUBMIT
                : ADDITIONAL_VEHICLE_STEPS.GARAGE;
            dispatch(changeAdditionalsStep(step));
            if (
              tempStandardItem.vehicleTypeCode.parentCategory ===
              STANDARD_ITEM_PARENT_CATEGORY.FED
            ) {
              dispatch(setSelectedVehicleType(tempStandardItem.vehicleType));
            } else {
              dispatch(
                setSelectedVehicleType(
                  tempStandardItem.vehicleTypeCode.parentCode,
                ),
              );
            }

            dispatch(setLastSin(tempStandardItem.standardItemNumber));
          } else {
            setNotFound(true);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      dispatch(resetAdditionalsState());
    }
  }, []);

  if (loading || additionalRequestLoading)
    return <Spinner data-testid="loading-spinner" className="padding-y-9" />;

  const isCreateRequest =
    (!requestId && !selectedCustomerAccount?.customerAccountId) ||
    isCustomerChange;

  if (notFound) {
    return (
      <NotFound
        goBackLink={{
          href: '/lease',
          text: 'Go back to lease requisitions start',
        }}
      />
    );
  }

  return (
    <>
      <Breadcrumbs />
      <div>
        <PageTitle
          title={isCreateRequest ? 'Create new request' : 'Additionals request'}
        />
        <div className="margin-top-neg-1">
          Additional requests will be reviewed and evaluated by your agency and
          GSA Fleet. All requests are subject <br /> to funding availability,
          agency policy, and may be filled using existing inventory.
        </div>
        {isCreateRequest ? <SearchCustomerAccount /> : <VehicleRequestSteps />}
      </div>
    </>
  );
}
